<template>
  <div class="row front">
    <div
      :class="['me-xl-0', 'connection-box', 'bottom-md', handleFrontClass]"
      name="step4-check-front"
      ref="webcam_box"
      v-if="showFront">
      <div class="before">
        <button
          type="button"
          class="btn btn-dark btn-lg icon icon-connection"
          @click="handelFrontConnected"
          :disabled="connectDisabled"
          name="connect-webcam">
          {{
            reconnect
              ? $t('preRegistration.step4.front.reconnect')
              : $t('preRegistration.step4.front.connect')
          }}
        </button>
        <p class="guide-text">
          {{
            reconnect
              ? $t('preRegistration.step4.front.guide.reconnect')
              : $t('preRegistration.step4.front.guide.connect')
          }}
        </p>
      </div>
      <div class="after">
        <div class="video-box">
          <div class="text-item video-text">
            <p>{{ $t('preRegistration.step4.front.ready') }}</p>
          </div>
          <video
            v-if="faceEvent"
            autoplay
            playsinline
            muted
            class="face_video reverse"
            ref="front_mediapipe"></video>
          <video
            v-if="frontMonitoring"
            autoplay
            playsinline
            muted
            class="face_video reverse"
            id="front_id"></video>
        </div>
      </div>
      <div class="cam-print-done">
        <div class="icon-confirm">
          <p>{{ $t('preRegistration.step4.front.connectComplete') }}</p>
        </div>
      </div>
    </div>
    <div v-if="phase === 3" class="col-4 -box bottom-md">
      <div class="box -box top-md">
        <p class="title">{{ $t('preRegistration.step4.front.env') }}</p>
        <ul class="info">
          <li>
            <b>OS</b> {{ userAgent.os.name ?? '-' }} |
            {{ userAgent.os.version ?? '-' }}
          </li>
          <li>
            <b>Browser</b> {{ userAgent.browser.name ?? '-' }} |
            {{ userAgent.browser.version ?? '-' }}
          </li>
          <li>
            <b>Device</b> {{ userAgent.device.vendor ?? '-' }} |
            {{ userAgent.device.model ?? '-' }} |
            {{ userAgent.device.type ?? '-' }}
          </li>
          <li>
            <b>Engine</b>{{ userAgent.engine.name ?? '-' }} |
            {{ userAgent.engine.version ?? '-' }}
          </li>
          <li><b>CPU</b>{{ userAgent.cpu.architecture ?? '-' }}</li>
          <li><b>Speed</b>{{ speed ?? '-' }}</li>
        </ul>
      </div>
      <Permission @handleDenied="handleDenied" />
    </div>
  </div>
</template>

<script>
  import Mediapipe from '@/js/video/media_pipe';
  import AntMediaConnectCheck from '@/js/video/antmedia/AntMediaConnectCheck';
  import {
    DEVICE_TYPE,
    CONNECT_CHECK_PHASE,
    VIDEO_SIZE,
    DEFAULT_VIDEO_CHECK_TIMEOUT,
    USER_LOG_TYPE,
    USER_LOG_STATUS,
  } from '@/js/common/constants';
  import {getStream, stopLocalCam} from '@/js/utils/camera';
  import Permission from '@/components/default/Permission';
  import {debounce, isEqual} from 'lodash';
  import {UAParser} from 'ua-parser-js';
  import {
    updateLogNavigator,
    updateLogStreamsAndDevice,
    updateLogPermission,
    sendLog,
    updateLogLoadFRModel,
  } from '@/js/log';

  export default {
    name: 'Front',
    components: {
      Permission,
    },
    props: {
      userId: {
        type: Number,
      },
      roomId: {
        type: String,
      },
      frontMonitoring: {
        type: Boolean,
        default: false,
      },
      phase: {
        type: Number,
      },
      active: {
        type: Boolean,
        default: false,
      },
      done: {
        type: Boolean,
        default: false,
      },
      faceEvent: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        webRTCAdaptor: null,
        timeoutId: null,

        connectDisabled: false,
        reconnect: false,

        // ua
        userAgent: {
          ua: '',
          browser: {
            name: '',
            version: '',
            major: '',
            type: '',
          },
          cpu: {
            architecture: '',
          },
          device: {
            vendor: '',
            model: '',
            type: '',
          },
          engine: {
            name: '',
            version: '',
          },
          os: {
            name: '',
            version: '',
          },
        },

        speed: null,
      };
    },

    mounted() {
      // Logs navigator 저장 호출
      updateLogNavigator();

      this.getUserAgent();
      this.getSpeed();
    },

    methods: {
      handelFrontConnected() {
        // Logs Permission 호출
        updateLogPermission(
          this.userId,
          this.frontMonitoring,
          USER_LOG_TYPE.FRONT_PRE,
        );

        this.connectDisabled = true;
        this.debounceConnected();
      },

      debounceConnected: debounce(async function () {
        const {stream, deviceId} = await getStream(
          this.userId,
          null,
          this.frontMonitoring,
        );
        // Logs Stream & Device 호출
        updateLogStreamsAndDevice(stream);

        if (!stream || !deviceId) {
          this.connectDisabled = false;
          return;
        }

        this.$emit('changeFrontActive', true);

        if (this.faceEvent) {
          const frontVideo = this.$refs['front_mediapipe'];
          frontVideo.srcObject = stream;
          this.openMediapipe(frontVideo);
        } else {
          updateLogLoadFRModel(false);
        }

        if (this.frontMonitoring) {
          this.openWebRTC(deviceId);
          return;
        }
      }, 1000),

      openMediapipe(video) {
        this.mediapipe = new Mediapipe({
          page: 'step4',
          drawUse: true,
          videoRef: video,
          width: VIDEO_SIZE.WIDTH,
          height: VIDEO_SIZE.HEIGHT,
          userId: this.userId,
          frBypass: false,
          detectTab: false,
          audio: this.frontMonitoring,
          successModelLoad: () => {
            // logs FRModel 성공시
            updateLogLoadFRModel(true);

            // 정면 사용하면 Webrtc에서 5초체크
            if (this.frontMonitoring) return;
            this.startTimer();
          },
          failModelLoad: () => {
            // logs FRModel 실패시
            updateLogLoadFRModel(false);

            this.startTimer();
          },
        });
      },

      openWebRTC(deviceId) {
        const {
          userId,
          roomId: videoRoomId,
          startTimer,
          handlePublishFinished,
          close,
          onDeviceNotSupported,
        } = this;

        this.webRTCAdaptor = new AntMediaConnectCheck({
          videoRoomId,
          publishType: DEVICE_TYPE['WEBCAM'],
          userId,
          deviceId,
          localVideoId: 'front_id',
          isPlayMode: false,
          startTimer,
          handlePublishFinished,
          onDeviceNotSupported,
          close,
        });
      },

      startTimer() {
        this.timeoutId = setTimeout(() => {
          if (this.frontMonitoring) {
            this.closeWebRTC();
          }

          this.$emit('changeFrontActive', false);

          this.stopMedia();

          this.$emit('connectStatusChanged', DEVICE_TYPE['WEBCAM'], true);
        }, DEFAULT_VIDEO_CHECK_TIMEOUT);
      },

      onDeviceNotSupported: () => {
        this.showModal(null, this.$t('error.deviceNotSupported'));
        this.hideModal(null, () => this.close());
      },

      stopMedia() {
        if (!this.faceEvent) return;

        this.mediapipe.cancelRAF();

        const video = this.$refs['front_mediapipe'];
        if (!video.srcObject) return;
        stopLocalCam(video);
      },

      handlePublishFinished() {
        this.webRTCAdaptor.closeWebSocket();
      },

      closeWebRTC() {
        this.webRTCAdaptor.stop();
        this.webRTCAdaptor.closeStream();
        this.webRTCAdaptor.closePeerConnection();
      },

      close() {
        this.$emit('changeFrontActive', false);
        clearTimeout(this.timeoutId);
        this.reconnect = true;
        this.connectDisabled = false;

        if (this.webRTCAdaptor) {
          this.closeWebRTC();
        }

        this.stopMedia();
      },

      handleDenied() {
        if (!this.active) return;

        this.close();
      },

      getUserAgent() {
        const uap = new UAParser();
        const userAgent = uap.getResult();
        this.userAgent = userAgent;
      },

      getSpeed() {
        const connection = navigator.connection;
        this.speed = connection.effectiveType;
      },
    },
    computed: {
      showFront() {
        return this.frontMonitoring || this.faceEvent;
      },
      handleFrontClass() {
        return {
          'tab-selected': this.phase === CONNECT_CHECK_PHASE['WEBCAM'],
          active: this.active,
          done: this.done,
        };
      },

      initFlags() {
        return this.$store.getters['user/getUserInitFlags'];
      },
    },

    watch: {
      initFlags: {
        handler(newVal, oldVal) {
          if (isEqual(newVal, oldVal)) {
            return;
          }

          const check = Object.values(newVal).some(value => value === false);

          if (check) return;

          const params = {
            userId: this.userId,
            logType: USER_LOG_TYPE.FRONT_PRE,
            status: USER_LOG_STATUS.INIT,
          };

          sendLog(params);
        },
        deep: true,
      },
    },
  };
</script>
